import { useEffect, useRef, useState } from "react";
import { Box, Button, CircularProgress, Typography } from "@mui/joy";
import { Camera, ChevronDown, ImageUp } from "lucide-react";

import { ImagePreviewModal } from "../../../../components/AlbumWidget/ImagePreviewDialog";
import { ImageUploadModal } from "../../../../components/AlbumWidget/ImageUploadModal";
import { useTask } from "../../../../hooks/query";
import { useNotification } from "../../../../hooks";
import { ImageWithMetadata } from "./types";
import { AuthenticatedImage } from "../../../";

interface MobileViewProps {
    imageGroups: any[];
    taskId: string;
    projectId: string;
    expandedAlbum: string | null;
    onToggleAlbum: (id: string) => void;
}

export function MobileView({ imageGroups, taskId, projectId, expandedAlbum, onToggleAlbum }: MobileViewProps) {
    return (
        <Box sx={{ mt: 1 }}>
            <Box sx={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 2 }}>
                {imageGroups.map((album: any) => (
                    <AlbumCard
                        key={album.id}
                        album={album}
                        isExpanded={expandedAlbum === album.id}
                        onToggle={() => onToggleAlbum(album.id)}
                        taskId={taskId}
                        projectId={projectId}
                    />
                ))}
            </Box>
        </Box>
    );
}

interface AlbumCardProps {
    album: any;
    isExpanded: boolean;
    onToggle: () => void;
    taskId: string;
    projectId: string;
}

function AlbumCard({ album, isExpanded, onToggle, taskId, projectId }: AlbumCardProps) {
    const photoCount = album.images?.length ?? 0;

    return (
        <Box
            sx={{
                gridColumn: isExpanded ? "1 / -1" : "auto",
                ...(isExpanded && {
                    bgcolor: "background.level1",
                    borderRadius: "14px",
                    border: "1px solid",
                    borderColor: "divider",
                    p: 0.75,
                }),
            }}
        >
            {/* Cover image thumbnail */}
            <AuthenticatedImage
                assetId={photoCount > 0 ? album.images[0].assetId : undefined}
                url={photoCount > 0 ? undefined : album.coverImage}
                isBackground
                onClick={onToggle}
                sx={coverCardSx(isExpanded)}
                borderRadius={isExpanded ? "10px" : "12px"}
            >
                <GradientOverlay isExpanded={isExpanded} />
                {!isExpanded && photoCount > 0 && <PhotoCountBadge count={photoCount} />}
                <TitleOverlay title={album.title} isExpanded={isExpanded} />
            </AuthenticatedImage>

            {/* Expanded: photo grid + upload */}
            {isExpanded && (
                <Box
                    sx={{
                        mt: 1,
                        animation: "fadeSlideIn 0.2s ease-out",
                        "@keyframes fadeSlideIn": {
                            from: { opacity: 0, transform: "translateY(-4px)" },
                            to: { opacity: 1, transform: "translateY(0)" },
                        },
                    }}
                >
                    <ExpandedPhotoGrid
                        images={album.images}
                        label={album.label}
                        taskId={taskId}
                        projectId={projectId}
                    />
                </Box>
            )}
        </Box>
    );
}

interface ExpandedPhotoGridProps {
    images: ImageWithMetadata[];
    label: string;
    taskId: string;
    projectId: string;
}

function ExpandedPhotoGrid({ images: initialImages, label, taskId, projectId }: ExpandedPhotoGridProps) {
    const { useUploadAsset } = useTask();
    const uploadAssetSvc = useUploadAsset();
    const { showError, showSuccess } = useNotification();

    const [images, setImages] = useState<ImageWithMetadata[]>(initialImages || []);
    const [previewImage, setPreviewImage] = useState<ImageWithMetadata | null>(null);
    const [uploadModalOpen, setUploadModalOpen] = useState(false);
    const [pendingFiles, setPendingFiles] = useState<File[]>([]);
    const [uploadedNames, setUploadedNames] = useState<Record<string, boolean>>({});
    const fileInputRef = useRef<HTMLInputElement>(null);

    // Sync when parent images change
    useEffect(() => {
        if (initialImages?.length) setImages(initialImages);
    }, [initialImages]);

    /* ── Upload logic ── */

    const uploadFiles = async (files: File[], note: string = "") => {
        let ok = 0;
        let fail = 0;

        for (const file of files) {
            if (uploadedNames[file.name]) continue;
            try {
                const data = new FormData();
                data.append("File", file);
                data.append("Label", label);
                data.append("TaskId", taskId);
                data.append("FileName", file.name);
                data.append("ProjectId", projectId);
                if (note) data.append("Note", note);

                await uploadAssetSvc.mutateAsync(data);
                setUploadedNames((prev) => ({ ...prev, [file.name]: true }));
                ok++;
            } catch {
                fail++;
            }
        }

        if (ok > 0) showSuccess(ok === 1 ? "Image uploaded" : `${ok} images uploaded`);
        if (fail > 0) showError(`Failed to upload ${fail} image(s)`);
    };

    const handleFileInput = (e: React.ChangeEvent<HTMLInputElement>) => {
        const files = Array.from(e.target.files || []);
        if (files.length > 0) {
            setPendingFiles(files);
            setUploadModalOpen(true);
        }
        setTimeout(() => {
            if (fileInputRef.current) fileInputRef.current.value = "";
        }, 0);
    };

    const handleUploadConfirm = async (files: File[], note: string) => {
        // Optimistic preview
        const previews: ImageWithMetadata[] = files.map((f) => ({
            url: URL.createObjectURL(f),
            note: note || null,
        }));
        setImages((prev) => [...prev, ...previews]);
        await uploadFiles(files, note);
    };

    /* ── Render ── */

    const hasPhotos = images.length > 0;

    return (
        <Box sx={{ mt: 1 }}>
            {/* Header / Meta */}
            {hasPhotos && (
                <Typography level="body-xs" sx={{ color: "text.tertiary", mb: 0.5, px: 0.5, fontWeight: 500 }}>
                    {images.length} photo{images.length !== 1 ? "s" : ""}
                </Typography>
            )}

            {/* Photo thumbnail grid */}
            {hasPhotos && (
                <Box
                    sx={{
                        display: "grid",
                        gridTemplateColumns: "repeat(3, 1fr)",
                        gap: 0.5,
                        borderRadius: "8px",
                        overflow: "hidden",
                    }}
                >
                    {images.map((image, i) => (
                        <PhotoThumbnail key={i} image={image} index={i} onPreview={setPreviewImage} />
                    ))}
                </Box>
            )}

            {/* Upload button */}
            <Box sx={{ mt: 1.5 }}>
                <input
                    ref={fileInputRef}
                    type="file"
                    accept="image/jpeg,image/png"
                    multiple
                    style={{ display: "none" }}
                    onChange={handleFileInput}
                />
                <Button
                    variant="soft"
                    color="primary"
                    fullWidth
                    size="md"
                    startDecorator={<ImageUp size={18} />}
                    onClick={() => {
                        if (fileInputRef.current) fileInputRef.current.value = "";
                        setTimeout(() => fileInputRef.current?.click(), 10);
                    }}
                    sx={{
                        borderRadius: "10px",
                        py: 1,
                        fontSize: "14px",
                        fontWeight: 600,
                    }}
                >
                    Upload Photos
                </Button>
            </Box>

            {/* Modals */}
            <ImagePreviewModal image={previewImage} onClose={() => setPreviewImage(null)} />
            <ImageUploadModal
                open={uploadModalOpen}
                files={pendingFiles}
                onClose={() => {
                    setUploadModalOpen(false);
                    setPendingFiles([]);
                }}
                onUpload={handleUploadConfirm}
            />
        </Box>
    );
}

function PhotoThumbnail({
    image,
    index,
    onPreview,
}: {
    image: ImageWithMetadata;
    index: number;
    onPreview: (img: ImageWithMetadata) => void;
}) {
    const [loading, setLoading] = useState(true);

    return (
        <Box
            onClick={() => onPreview(image)}
            sx={{
                position: "relative",
                aspectRatio: "1/1",
                cursor: "pointer",
                bgcolor: "background.level2",
                "&:active": { opacity: 0.7 },
            }}
        >
            {loading && (
                <Box
                    sx={{
                        position: "absolute",
                        inset: 0,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                    }}
                >
                    <CircularProgress size="sm" />
                </Box>
            )}
            <AuthenticatedImage
                assetId={image.assetId}
                url={image.url}
                alt={`Photo ${index + 1}`}
                onLoad={() => setLoading(false)}
                sx={{
                    width: "100%",
                    height: "100%",
                    opacity: loading ? 0 : 1,
                    transition: "opacity 0.2s ease",
                }}
            />
        </Box>
    );
}

function GradientOverlay({ isExpanded }: { isExpanded?: boolean }) {
    return (
        <Box
            sx={{
                position: "absolute",
                inset: 0,
                background: isExpanded
                    ? "rgba(0,0,0,0.55)"
                    : "linear-gradient(180deg, transparent 40%, rgba(0,0,0,0.7) 100%)",
                backdropFilter: isExpanded ? "blur(2px)" : "none",
            }}
        />
    );
}

function PhotoCountBadge({ count }: { count: number }) {
    return (
        <Box
            sx={{
                position: "absolute",
                top: 8,
                right: 8,
                bgcolor: "rgba(0,0,0,0.5)",
                borderRadius: "16px",
                px: 0.75,
                py: 0.25,
                display: "flex",
                alignItems: "center",
                gap: 0.4,
                backdropFilter: "blur(4px)",
            }}
        >
            <Camera size={10} color="white" />
            <Typography sx={{ color: "white", fontSize: "10px", fontWeight: 700, lineHeight: 1 }}>{count}</Typography>
        </Box>
    );
}

function TitleOverlay({ title, isExpanded }: { title: string; isExpanded: boolean }) {
    return (
        <Box
            sx={{
                position: "absolute",
                inset: 0,
                px: 1,
                display: "flex",
                alignItems: isExpanded ? "center" : "flex-end",
                justifyContent: "space-between",
                pb: isExpanded ? 0 : 1,
            }}
        >
            <Typography
                noWrap={isExpanded}
                sx={{
                    color: "white",
                    fontSize: isExpanded ? "12px" : "14px",
                    fontWeight: 600,
                    lineHeight: 1.2,
                    textShadow: "0 1px 3px rgba(0,0,0,0.5)",
                    flex: 1,
                    minWidth: 0,
                    ...(!isExpanded && {
                        display: "-webkit-box",
                        WebkitLineClamp: 2,
                        WebkitBoxOrient: "vertical",
                        overflow: "hidden",
                    }),
                }}
            >
                {title}
            </Typography>
            <ChevronDown
                size={14}
                color="white"
                style={{
                    flexShrink: 0,
                    transform: isExpanded ? "rotate(180deg)" : "rotate(0deg)",
                }}
            />
        </Box>
    );
}

const coverCardSx = (isExpanded: boolean) => ({
    position: "relative",
    borderRadius: isExpanded ? "10px" : "12px",
    overflow: "hidden",
    cursor: "pointer",
    ...(isExpanded
        ? { height: 36, backgroundPosition: "center top" }
        : { aspectRatio: "1/1", backgroundPosition: "center" }),
    backgroundSize: "cover",
    backgroundPosition: "center",
});
